import React from "react";
import _ from "lodash";
import cx from "classnames";
import { Button, Box } from "@showitinc/showit-wc";
import Editor from "@monaco-editor/react";

import SiteDesignStore from "../stores/SiteDesignStore";
import UIActions from "../actions/UIActions";
import DocumentStore from "../document/DocumentStore";
import { COMMANDS } from "../document/commands";
import Modal from "./base/Modal";
import ButtonBar from "./base/ButtonBar";
import uiPrefix from "../utils/UiPrefixUtils";
import { applyEditorFixes } from "@/utils/MonacoEditorFixes.mjs";
import { findElementRange } from "@/utils/ElementJsonLocator";

let _pendingElementContext = null;

export function setInspectElementContext(context) {
  _pendingElementContext = context;
}

class JSONEditor extends React.Component {
  constructor(props) {
    super(props);

    this.elementContext = _pendingElementContext;
    _pendingElementContext = null;

    const tab = this.elementContext?.tab === "site" ? "site" : "page";
    const source =
      tab === "site" ? SiteDesignStore.getDesign() : SiteDesignStore.getCurrentPage();
    const json = JSON.stringify(source.data.toJS(), null, 4);

    this.state = { json, tab };

    this.editorRef = null;
  }

  componentDidMount() {
    this.storeUnsubscribe = SiteDesignStore.listen(this._onSiteDesignChange);
  }

  componentWillUnmount() {
    if (this.storeUnsubscribe) {
      _.attempt(this.storeUnsubscribe);
    }

    this.storeUnsubscribe = null;
    this.editorRef = null;
  }

  _selectTab = (tab) => {
    if (tab !== this.state.tab) {
      this.setState({ tab });
      this._loadJSON(tab);
    }
  };

  _onSiteDesignChange = () => {
    this._loadJSON(this.state.tab);
  };

  _loadJSON = (tab) => {
    let obj = null;

    if (tab === "page") {
      obj = SiteDesignStore.getCurrentPage().data;
    } else if (tab === "site") {
      obj = SiteDesignStore.getDesign().data;
    }

    if (obj != null) {
      const json = JSON.stringify(obj.toJS(), null, 4);

      this.setState({
        json,
      });
    }
  };

  _saveJSON = () => {
    const { debugMode } = this.props;
    const { json } = this.state;
    try {
      const obj = JSON.parse(json);
      DocumentStore.dispatch({ type: COMMANDS.SAVE_JSON, payload: { object: obj } });

      if (!debugMode) {
        UIActions.toggleJSONEditor();
      }
    } catch (e) {
      console.log(e);
      alert(`Cannot be converted to JSON: ${e}`);
    }
  };

  _resetJSON = () => {
    this._onSiteDesignChange();
  };

  _onJSONChange = (json) => {
    this.setState({ json });
  };

  _onEditorMount = (editor) => {
    applyEditorFixes(editor);
    this.editorRef = editor;

    if (this.elementContext) {
      requestAnimationFrame(() => this._highlightElement());
    }
  };

  _highlightElement = () => {
    const editor = this.editorRef;
    const context = this.elementContext;

    if (!context || !editor) return;

    try {
      const range = findElementRange(context.idPath, this.state.json);
      editor.setSelection(range);
      editor.revealRangeInCenter(range);
      editor.focus();
    } catch (e) {
      console.warn("[JSONEditor] Could not locate element in JSON:", e.message);
    }
  };

  render() {
    let modalProps = _.pick(this.props, Modal.BASIC_PROPS);

    let editorStyle = {
      display: "flex",
      minHeight: 0,
      justifyContent: "flex-start",
      alignItems: "stretch",
      alignContent: "stretch",
      flex: "1 1 auto",
      position: "relative",
      height: "100%",
    };

    let tabStyle = {
      width: "auto",
      padding: "8px 16px",
      flexGrow: 0,
    };

    let siteTab = cx({
      tab: true,
      active: this.state.tab === "site",
    });

    let pageTab = cx({
      tab: true,
      active: this.state.tab === "page",
    });

    return (
      <Modal {...modalProps}>
        <Modal.Dialog size="large">
          <Modal.Dialog.Header closeButton>
            <Modal.Dialog.Title>Site/Page Editor</Modal.Dialog.Title>
          </Modal.Dialog.Header>
          <Modal.Dialog.Body
            style={{
              height: "100%",
              width: "100%",
              position: "relative",
              padding: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div style={{ padding: 5 }}>
              Only Active Tab Saves, changing tabs resets back to original
              structure
            </div>
            <div
              className="properties-tabset-wrapper"
              style={{ marginBottom: 10 }}
            >
              <div className="properties-tabset" style={{ margin: 0 }}>
                <div
                  className={pageTab}
                  style={tabStyle}
                  onClick={this._selectTab.bind(this, "page")}
                >
                  Page
                </div>
                <div
                  className={siteTab}
                  style={tabStyle}
                  onClick={this._selectTab.bind(this, "site")}
                >
                  Site
                </div>
              </div>
            </div>
            <div style={editorStyle}>
              <Editor
                defaultLanguage="json"
                value={this.state.json}
                theme="vs-dark"
                onChange={this._onJSONChange}
                onMount={this._onEditorMount}
              />
            </div>
          </Modal.Dialog.Body>
          <Modal.Dialog.Footer>
            <ButtonBar>
              <Box mr="3">
                <Button
                  variant="minimal"
                  data-e2e={uiPrefix("modal-close-btn")}
                  className={uiPrefix("modal-close-btn")}
                  onClick={this._resetJSON}
                >
                  Reset
                </Button>
              </Box>
              <Button
                variant="feature"
                data-e2e={uiPrefix("modal-action-btn")}
                className={uiPrefix("modal-action-btn")}
                onClick={this._saveJSON}
              >
                Save
              </Button>
            </ButtonBar>
          </Modal.Dialog.Footer>
        </Modal.Dialog>
      </Modal>
    );
  }
}

export default JSONEditor;
